import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { Users, MagnifyingGlass, WarningCircle, Moon, Church, Champagne } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';
import GuestDetailModal from '../GuestDetailModal';

const GuestListSection = ({ guests, weddingData }) => {
  const styles = useThemeStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterType, setFilterType] = useState('both'); // 'ceremony', 'reception', 'both'
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Anyone yes to ceremony or reception is automatically yes to evening for display/count
  const isEveningGuest = (g) =>
    g.invitedToCeremony !== false || g.invitedToReception !== false || g.invitedToEvening === true;

  // Calculate counts for each filter type
  const counts = useMemo(() => {
    if (!guests || guests.length === 0) {
      return { ceremony: 0, reception: 0, evening: 0, both: 0 };
    }
    
    const ceremonyCount = guests.filter(g => g.invitedToCeremony !== false && !g.invitedToEvening).length;
    const receptionOnlyCount = guests.filter(g => g.invitedToReception !== false && !g.invitedToEvening).length;
    const eveningCount = guests.filter(g => isEveningGuest(g)).length;
    // Total reception count includes both reception guests and evening guests (for capacity purposes)
    const receptionCount = receptionOnlyCount + eveningCount;
    const bothCount = guests.length;
    
    return { ceremony: ceremonyCount, reception: receptionCount, receptionOnly: receptionOnlyCount, evening: eveningCount, both: bothCount };
  }, [guests]);

  // Get capacities from wedding details
  const ceremonyCapacity = weddingData?.details?.venues?.ceremony?.capacity;
  const receptionCapacity = weddingData?.details?.venues?.reception?.capacity;
  const eveningCapacity = weddingData?.details?.venues?.reception?.eveningCapacity;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredGuests = useMemo(() => {
    let filtered = guests?.filter(guest => {
      // Search filter (only search by name for suppliers, no email)
      const matchesSearch = guest.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;

      // Invitation type filter
      if (filterType === 'ceremony') {
        return guest.invitedToCeremony !== false && !guest.invitedToEvening;
      } else if (filterType === 'reception') {
        return guest.invitedToReception !== false && !guest.invitedToEvening;
      } else if (filterType === 'evening') {
        return isEveningGuest(guest);
      } else {
        // 'both' - show all guests
        return true;
      }
    }) || [];

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortField) {
          case 'ceremony':
            aValue = a.invitedToCeremony !== false && !a.invitedToEvening ? 1 : 0;
            bValue = b.invitedToCeremony !== false && !b.invitedToEvening ? 1 : 0;
            break;
          case 'reception':
            aValue = a.invitedToReception !== false && !a.invitedToEvening ? 1 : 0;
            bValue = b.invitedToReception !== false && !b.invitedToEvening ? 1 : 0;
            break;
          case 'evening':
            aValue = isEveningGuest(a) ? 1 : 0;
            bValue = isEveningGuest(b) ? 1 : 0;
            break;
          default:
            return 0;
        }
        
        if (sortDirection === 'asc') {
          return bValue - aValue; // Reverse: Yes (1) comes first in ascending
        } else {
          return aValue - bValue; // Reverse: No (0) comes first in descending
        }
      });
    }

    return filtered;
  }, [guests, searchTerm, filterType, sortField, sortDirection]);

  const handleGuestClick = (guest) => {
    setSelectedGuest(guest);
    setModalOpen(true);
  };

  const _getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return '✅';
      case 'declined':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getCapacityColor = (count, capacity) => {
    if (!capacity) return 'default';
    const percentage = (count / capacity) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 85) return 'warning';
    return 'success';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Users size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Guest List
        </Typography>
      </Box>

      {/* Count and Capacity Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              ...styles.card, 
              flex: 1,
              display: 'flex',
              bgcolor: filterType === 'both' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('both')}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 0.5 }}>
                    Total Guests
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, ...styles.text.primary }}>
                    {counts.both}
                  </Typography>
                </Box>
                <Users size={32} style={{ color: '#64748b', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              ...styles.card, 
              bgcolor: filterType === 'ceremony' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('ceremony')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 0.5 }}>
                    Ceremony Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Church size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, ...styles.text.primary }}>
                      {counts.ceremony}
                    </Typography>
                  </Box>
                  {ceremonyCapacity && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ ...styles.text.secondary }}>
                        Capacity:
                      </Typography>
                      <Chip
                        label={`${counts.ceremony} / ${ceremonyCapacity}`}
                        size="small"
                        color={getCapacityColor(counts.ceremony, ceremonyCapacity)}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              ...styles.card, 
              bgcolor: filterType === 'reception' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('reception')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 0.5 }}>
                    Reception Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Champagne size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, ...styles.text.primary }}>
                      {counts.receptionOnly}
                    </Typography>
                  </Box>
                  {receptionCapacity && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ ...styles.text.secondary }}>
                        Capacity:
                      </Typography>
                      <Chip
                        label={`${counts.receptionOnly} / ${receptionCapacity}`}
                        size="small"
                        color={getCapacityColor(counts.receptionOnly, receptionCapacity)}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              ...styles.card, 
              bgcolor: filterType === 'evening' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('evening')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 0.5 }}>
                    Evening Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Moon size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, ...styles.text.primary }}>
                      {counts.evening}
                    </Typography>
                  </Box>
                  {eveningCapacity && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ ...styles.text.secondary }}>
                        Capacity:
                      </Typography>
                      <Chip
                        label={`${counts.evening} / ${eveningCapacity}`}
                        size="small"
                        color={getCapacityColor(counts.evening, eveningCapacity)}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={20} />
              </InputAdornment>
            )
          }}
          sx={{ ...styles.input }}
        />
      </Box>

      {sortedAndFilteredGuests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            {searchTerm ? 'No guests found matching your search' : 'No guests added yet'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ ...styles.card }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>
                  <Tooltip title="Sort by Ceremony">
                    <TableSortLabel
                      active={sortField === 'ceremony'}
                      direction={sortField === 'ceremony' ? sortDirection : 'asc'}
                      onClick={() => handleSort('ceremony')}
                    >
                      <Church size={18} />
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Sort by Reception">
                    <TableSortLabel
                      active={sortField === 'reception'}
                      direction={sortField === 'reception' ? sortDirection : 'asc'}
                      onClick={() => handleSort('reception')}
                    >
                      <Champagne size={18} />
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Sort by Evening">
                    <TableSortLabel
                      active={sortField === 'evening'}
                      direction={sortField === 'evening' ? sortDirection : 'asc'}
                      onClick={() => handleSort('evening')}
                    >
                      <Moon size={18} />
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredGuests.map((guest) => (
                <TableRow
                  key={guest._id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleGuestClick(guest)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'primary.main'
                        }}
                      >
                        {getInitials(guest.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {guest.name}
                        </Typography>
                        {guest.plusOne && (
                          <Chip label="+1" size="small" variant="outlined" sx={{ mt: 0.5, height: 18 }} />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {guest.invitedToCeremony !== false && !guest.invitedToEvening ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>
                    {guest.invitedToReception !== false && !guest.invitedToEvening ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>
                    {isEveningGuest(guest) ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>
                    {guest.status && (
                      <Chip
                        label={guest.status}
                        size="small"
                        color={getStatusColor(guest.status)}
                      />
                    )}
                    {guest.dietaryRequirements && (
                      <Chip
                        icon={<WarningCircle size={14} />}
                        label="Dietary"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ ...styles.text.secondary, cursor: 'pointer' }}>
                      Click to view details
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedGuest && (
        <GuestDetailModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedGuest(null);
          }}
          guest={selectedGuest}
        />
      )}
    </Box>
  );
};

export default GuestListSection;
